import {
  Intake,
  OrdonnanceCategory,
  OrdonnanceMedicine,
  OrdonnanceTemplate,
  TaskDuration,
  medicineHoraires,
  medicineUnits,
  ordonnanceCategories,
  taskDurations,
} from "@/src/data/allTypes";
import apiFetch from "@/src/utils/apiFetch";

import { getUserId, jsonAbstract, uid } from "@/src/utils/utils";
import { useState } from "react";
import { MedicinesChoiceList } from "./medicinesHook";

import { Button } from "@/src/components/buttons";
import { Card } from "@/src/components/cards";
import { closeDrawer, openDrawer } from "@/src/components/dialogs";
import { FetchableList } from "@/src/components/fetchableLists";
import { Input } from "@/src/components/universalInput";
import { CardType } from "@/src/data/allTypes";
import { MdOutlineDragIndicator } from "react-icons/md";
import { OrdonnanceMedicineForm } from "./ordonnanceMedicineForm";

function OrdonnanceTemplateChoiceList({
  categories,
  onSelect,
}: {
  categories?: OrdonnanceCategory[];
  onSelect: (newValue: OrdonnanceTemplate) => void;
}) {
  const [reload, setReload] = useState(0);
  let [searchKey, setSearchKey] = useState("");

  return (
    <div className="grid c1 gap10">
      <FetchableList
        queryKey={["OrdonnanceTemplateChoiceList", searchKey, reload]}
        queryFn={() =>
          apiFetch(
            "ordonnanceTemplates",
            "loadMany",
            { searchKey, categories },
            "Erreur lors du chargement des modèles d'ordonnance"
          )
        }
        searchKey={searchKey}
        onSearch={(newValue) => {
          setSearchKey(newValue || "");
        }}
        onClick={async () => {
          let template: OrdonnanceTemplate = {
            name: `${searchKey || ""}`,
            ordonnanceMedicines: [] as OrdonnanceMedicine[],
            category: categories?.[0] || "ambulatory",
            creator: getUserId(),
          };
          await openOrdonnanceTemplateForm({
            template,
            categories,
            onConfirm: (template) => {
              onSelect(template);
              setReload((prev) => prev + 1);
              setSearchKey("");
            },
          });
        }}
        emptyChild={
          searchKey && (
            <div className="center grid c1 gap10 p20">
              Aucun modèle disponible
              <Button
                onClick={async () => {
                  let template: OrdonnanceTemplate = {
                    name: `${searchKey || ""}`,
                    ordonnanceMedicines: [] as OrdonnanceMedicine[],
                    category: categories?.[0] || "ambulatory",
                    creator: getUserId(),
                  };
                  await openOrdonnanceTemplateForm({
                    template,
                    categories,
                    onConfirm: (template) => {
                      onSelect(template);
                      setReload((prev) => prev + 1);
                      setSearchKey("");
                    },
                  });
                }}
              >
                Créer le modèle {searchKey}
              </Button>
            </div>
          )
        }
      >
        {(data) => (
          <div className="grid c1 gap10">
            {data?.map((template, index) => {
              let type = ordonnanceCategories[template.category];
              return (
                <Card
                  key={uid()}
                  onClick={async () => {
                    onSelect(jsonAbstract(template) as OrdonnanceTemplate);
                  }}
                  color="purple"
                  title={template.name}
                  onEdit={async () => {
                    await openOrdonnanceTemplateForm({
                      template,
                      categories,
                      onConfirm: (template) => {
                        onSelect(template);
                        setReload((prev) => prev + 1);
                        setSearchKey("");
                      },
                    });
                    setReload((prev) => prev + 1);
                  }}
                >
                  <div className="gray">{type?.name}</div>
                </Card>
              );
            })}
          </div>
        )}
      </FetchableList>
    </div>
  );
}

function OrdonnanceTemplateForm({
  current,
  categories,
  onChange,
}: {
  current?: OrdonnanceTemplate;
  categories?: OrdonnanceCategory[];
  onChange: (newValue: OrdonnanceTemplate) => void;
}) {
  let [data, setData] = useState(current || ({} as OrdonnanceTemplate));
  function setNewValue(newValue: OrdonnanceTemplate) {
    setData(newValue);
    onChange(newValue);
  }

  categories ||= Object.keys(ordonnanceCategories) as OrdonnanceCategory[];

  return (
    <div className="grid gap10 c1">
      <Input
        name={"Nom"}
        type={"text"}
        isRequired={true}
        value={data.name}
        onChange={(newValue) => {
          setNewValue({ ...data, name: newValue });
        }}
      />
      <Input
        name={"Catégorie"}
        type={"select"}
        value={data.category}
        options={categories?.map((categoryId) => {
          let category = ordonnanceCategories[categoryId];
          return { value: category.id, label: category.name };
        })}
        onChange={(newValue) => {
          setNewValue({ ...data, category: newValue });
        }}
      />
      <ReorderableTemplateMedicines
        label={"Médicaments"}
        category={data.category}
        current={data.ordonnanceMedicines}
        onChange={(newValue) => {
          setNewValue({ ...data, ordonnanceMedicines: newValue });
        }}
      />
    </div>
  );
}

function ReorderableTemplateMedicines({
  label,
  category,
  current,
  onChange,
}: {
  label?: string | null | undefined;
  category: OrdonnanceCategory;
  current: (OrdonnanceMedicine | null | undefined)[];
  onChange: (newValue: (OrdonnanceMedicine | null | undefined)[]) => void;
}) {
  let [ordonnanceMedicines, setOrdonnanceMedicines] = useState(current || []);
  let [reload, setReload] = useState(0);

  function onValueChange(newValue: (OrdonnanceMedicine | null | undefined)[]) {
    setOrdonnanceMedicines([...newValue]);
    onChange([...newValue]);
  }

  async function openOrdonnanceMedicineForm(
    ordonnanceMedicine: OrdonnanceMedicine,
    index: number
  ) {
    await openDrawer({
      title: "Ajouter un médicament",
      additionalClasses: ["small"],
      content: (
        <OrdonnanceMedicineForm
          isTemplate={true}
          category={category}
          current={ordonnanceMedicine}
          onChange={(newValue) => {
            ordonnanceMedicine = newValue;
          }}
        />
      ),
      onConfirm: async () => {
        if (index == -1) ordonnanceMedicines.push(ordonnanceMedicine);
        else ordonnanceMedicines[index] = ordonnanceMedicine;

        closeDrawer("medicineChoiceDrawer");
        onValueChange(ordonnanceMedicines);
      },
    });
  }

  return (
    <div className="field">
      {label ? <div className="label">{label}</div> : null}
      <div className="grid c1 gap10">
        {ordonnanceMedicines?.map((ordonnanceMedicine, index) => {
          if (!ordonnanceMedicine) return;
          return (
            <OrdonnanceMedicineCard
              key={uid()}
              ordonnanceMedicine={ordonnanceMedicine}
              onClick={() => {
                openOrdonnanceMedicineForm(ordonnanceMedicine, index);
              }}
            ></OrdonnanceMedicineCard>
          );
        })}

        <Button
          type={"tertiary"}
          onClick={() => {
            openDrawer({
              id: "medicineChoiceDrawer",
              title: "Choisir un médicament",
              content: (
                <MedicinesChoiceList
                  onSelect={async (medicine) => {
                    let ordonnanceMedicine: OrdonnanceMedicine = {
                      medicine,
                      notes: "",
                      creator: getUserId(),
                      intakes: [] as Intake[],
                      tasks: [],
                      
                    };

                    await openOrdonnanceMedicineForm(ordonnanceMedicine, -1);
                  }}
                />
              ),
            });
          }}
        >
          Ajouter un médicament
        </Button>
      </div>
    </div>
  );
}

export function openTemplateChoice({
  categories,
  onSelect,
}: {
  categories?: OrdonnanceCategory[];
  onSelect: (newValue: OrdonnanceTemplate) => void;
}) {
  openDrawer({
    id: "ordonnanceTemplateDrawer",
    title: "Choisir un modèle d'ordonnance",
    content: (
      <OrdonnanceTemplateChoiceList
        categories={categories}
        onSelect={async (template) => {
          onSelect(template);
        }}
      />
    ),
    footer: <div></div>,
  });
}

export async function openOrdonnanceTemplateForm({
  template,
  categories,
  onConfirm,
}: {
  template: OrdonnanceTemplate;
  categories?: OrdonnanceCategory[];
  onConfirm?: (newValue: OrdonnanceTemplate) => void;
}) {
  await openDrawer({
    id: "ordonnanceTemplateDrawer",
    title: "Ajouter un modèle d'ordonnance",
    content: (
      <OrdonnanceTemplateForm
        categories={categories}
        current={template}
        onChange={(newValue) => {
          template = newValue;
        }}
      />
    ),
    onConfirm: async () => {
      console.log("gonna saaaave", { template });
      let inserted = await apiFetch(
        "ordonnanceTemplates",
        "addOne",
        {
          template,
        },
        "Erreur lors de la création du modèle d'ordonnance"
      );
      if (!inserted) return;
      template = inserted;
      onConfirm?.(template);
    },
  });

  return template;
}

export function OrdonnanceMedicineCard({
  type,
  ordonnanceMedicine,
  onDelete,
  onClick,
}: {
  type?: CardType;
  ordonnanceMedicine: OrdonnanceMedicine;
  onDelete?: () => void;
  onClick?: () => void;
}) {
  let medicine = ordonnanceMedicine.medicine || {};

  return (
    <Card
      color={type || "purple"}
      leading={
        <div className="grab disabled">
          <MdOutlineDragIndicator size={15} />
        </div>
      }
      onClick={async () => {
        onClick?.();
      }}
      onDelete={async () => {
        onDelete?.();
      }}
    >
      <div className="c1 gap5">
        <div>
          <strong className="capitalize">
            {medicine?.name} {medicine?.dosage || ""}
          </strong>
          <span className="gray capitalize"> {medicine?.dci}</span>
        </div>

        {ordonnanceMedicine.intakes?.map((intake) => {
          if (!intake) return null;
          const {
            duration,
            dilutionVolume,
            solution,
            posology,
            unit,
            horaire,
            route,
            notes,
          } = intake;
          const horaireInfo = medicineHoraires[horaire];
          const unitInfo = medicineUnits[unit];
          const taskDurationInfo =
            taskDurations[intake?.duration as TaskDuration];
          return (
            <div key={uid()}>
              <div>
                <strong>{posology || ""}</strong> {unitInfo?.shortName}{" "}
                {horaireInfo.name} {route ? `par voie ${route}` : ""}
              </div>
              <div className="gray">
                {dilutionVolume != null && `Dilué dans ${dilutionVolume}ml`}
                {solution != null &&
                  ` de ${solution || "Aucune solution selectionnée"} `}
              </div>
              <div className="gray">
                {(duration && intake?.unit?.includes("/h") == true) ||
                intake.route == "IVL"
                  ? `à administrer dans une durée de ${
                      taskDurationInfo?.name || `${duration} minutes`
                    }`
                  : ""}
              </div>

              <div>{notes || ""}</div>
            </div>
          );
        })}
        <div>
          Pendant{" "}
          {ordonnanceMedicine?.duration || (
            <span className="br p5 redish">Veuillez spécifier la durée</span>
          )}
        </div>
      </div>
    </Card>
  );
}
